/**
 * @fileoverview Move-to dialog for the Documents Library.
 *
 * Indented folder tree built from `listFolders` (children keyed by
 * parentFolderId) with a "Library root" entry on top. Invalid targets are
 * disabled with a hint instead of letting the server throw: a moved
 * folder's own subtree, and — when moving folders — targets across the
 * synced/unsynced boundary (root counts as unsynced). Documents can move
 * anywhere.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useConvex } from "convex/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { ConvexError } from "convex/values";
import { isSameOrDescendantPath } from "@cvx/lib/folderPaths";
import { Folder, FolderRoot, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/ui/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { cn } from "@/utils/misc";

interface FolderRow {
  _id: Id<"folders">;
  name: string;
  parentFolderId?: Id<"folders">;
  path: string;
  connectionId?: string;
}

interface FolderPickerDialogProps {
  jobId: Id<"jobs">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Documents being moved. */
  documentIds: Id<"documents">[];
  /** Folders being moved (moved one at a time via moveFolder). */
  folderIds: Id<"folders">[];
}

/** Root target sentinel — moveDocuments/moveFolder treat undefined as root. */
const ROOT = "__root__";

/**
 * Move-to dialog. Confirm runs `moveDocuments` (arrays natively) plus one
 * `moveFolder` per selected folder, surfacing ConvexError messages as
 * toasts.
 */
export function FolderPickerDialog({
  jobId,
  open,
  onOpenChange,
  documentIds,
  folderIds,
}: FolderPickerDialogProps) {
  const convex = useConvex();
  const { data: folders } = useQuery(
    convexQuery(api.jobs.gatherInformation.folders.queries.listFolders, {
      jobId,
    }),
  );

  const [target, setTarget] = useState<string>(ROOT);
  const [isMoving, setIsMoving] = useState(false);

  // Reset the picked target each time the dialog opens.
  useEffect(() => {
    if (open) setTarget(ROOT);
  }, [open]);

  const allFolders = useMemo(() => folders ?? [], [folders]);
  const movedFolders = useMemo(
    () => allFolders.filter((f) => folderIds.includes(f._id)),
    [allFolders, folderIds],
  );

  // Indented rows in tree order (children sorted by name per level).
  const rows = useMemo(() => {
    const byParent = new Map<string, FolderRow[]>();
    for (const folder of allFolders) {
      const key = folder.parentFolderId ?? "";
      const siblings = byParent.get(key);
      if (siblings) siblings.push(folder);
      else byParent.set(key, [folder]);
    }
    for (const siblings of byParent.values()) {
      siblings.sort((a, b) => a.name.localeCompare(b.name));
    }

    const flattened: Array<{ folder: FolderRow; depth: number }> = [];
    const walk = (parentKey: string, depth: number) => {
      for (const folder of byParent.get(parentKey) ?? []) {
        flattened.push({ folder, depth });
        walk(folder._id, depth + 1);
      }
    };
    walk("", 0);
    return flattened;
  }, [allFolders]);

  const isMovingFolders = movedFolders.length > 0;

  /** Why a target is disabled (null = valid). */
  const disabledReason = (folder: FolderRow | null): string | null => {
    // Target inside (or equal to) a moved folder's subtree.
    if (folder) {
      for (const moved of movedFolders) {
        if (isSameOrDescendantPath(folder.path, moved.path)) {
          return moved._id === folder._id
            ? "This is the folder being moved"
            : "Inside the folder being moved";
        }
      }
    }
    // Folder moves cannot cross the synced boundary (root is unsynced).
    if (isMovingFolders) {
      const targetConnection = folder?.connectionId;
      for (const moved of movedFolders) {
        if (
          (targetConnection ?? undefined) !== (moved.connectionId ?? undefined)
        ) {
          return "Folders cannot move between synced and unsynced areas";
        }
      }
    }
    return null;
  };

  const rootReason = disabledReason(null);
  const selectedReason =
    target === ROOT
      ? rootReason
      : disabledReason(allFolders.find((f) => f._id === target) ?? null);

  const handleMove = async () => {
    if (isMoving || selectedReason) return;
    const folderId = target === ROOT ? undefined : (target as Id<"folders">);
    setIsMoving(true);
    try {
      if (documentIds.length > 0) {
        await convex.mutation(
          api.jobs.gatherInformation.documents.mutations.moveDocuments,
          { documentIds, folderId },
        );
      }
      for (const movedId of folderIds) {
        await convex.mutation(
          api.jobs.gatherInformation.folders.mutations.moveFolder,
          { folderId: movedId, newParentFolderId: folderId },
        );
      }
      const count = documentIds.length + folderIds.length;
      toast.success(`Moved ${count} item${count === 1 ? "" : "s"}`);
      onOpenChange(false);
    } catch (err) {
      toast.error(
        err instanceof ConvexError && typeof err.data === "string"
          ? err.data
          : "Failed to move items",
      );
    } finally {
      setIsMoving(false);
    }
  };

  const itemCount = documentIds.length + folderIds.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Move to…</DialogTitle>
          <DialogDescription>
            Choose a destination for {itemCount} item
            {itemCount === 1 ? "" : "s"}.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-72 overflow-y-auto rounded-md border p-1">
          <PickerRow
            depth={0}
            icon={
              <FolderRoot className="h-4 w-4 shrink-0 text-muted-foreground" />
            }
            label="Library root"
            isSelected={target === ROOT}
            reason={rootReason}
            onSelect={() => setTarget(ROOT)}
          />
          {rows.map(({ folder, depth }) => (
            <PickerRow
              key={folder._id}
              depth={depth + 1}
              icon={
                <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
              }
              label={folder.name}
              isSelected={target === folder._id}
              reason={disabledReason(folder)}
              onSelect={() => setTarget(folder._id)}
            />
          ))}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isMoving || selectedReason !== null}
            onClick={() => void handleMove()}
          >
            {isMoving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Move
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** One indented, selectable target row; disabled rows show their reason. */
function PickerRow({
  depth,
  icon,
  label,
  isSelected,
  reason,
  onSelect,
}: {
  depth: number;
  icon: React.ReactNode;
  label: string;
  isSelected: boolean;
  reason: string | null;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      disabled={reason !== null}
      onClick={onSelect}
      title={reason ?? undefined}
      className={cn(
        "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm",
        isSelected ? "bg-accent" : "hover:bg-accent/50",
        reason !== null && "cursor-not-allowed opacity-50 hover:bg-transparent",
      )}
      style={{ paddingLeft: `${8 + depth * 16}px` }}
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {reason !== null && (
        <span className="shrink-0 text-[10px] text-muted-foreground">
          {reason}
        </span>
      )}
    </button>
  );
}
